/**
 * Feedback visual centralizado — Etapa 3 (2026-09).
 *
 * Objetivo: acessibilidade total pra o Carlo e usuários no canteiro de obras.
 *
 *   - Sucesso: toast GRANDE, ícone check verde, mensagem clara em português simples
 *   - Erro: toast VERMELHO com botão "Tentar de novo" (CTA), mensagem explicativa
 *   - Loading: opcional (pra ações longas > 2s), com progress feedback
 *
 * Usa a lib `sonner` que já está no shadcn. Import único em App.js do `<Toaster/>`.
 */
import { toast as sonnerToast } from "sonner";

const BIG_STYLE = {
  fontSize: 16,
  padding: 18,
  minHeight: 72,
  border: "2px solid",
  borderRadius: 8,
};

const SUCCESS_STYLE = {
  ...BIG_STYLE,
  borderColor: "#3f7a4a",
  background: "#f0f7f1",
  color: "#1e3f26",
};

const ERROR_STYLE = {
  ...BIG_STYLE,
  borderColor: "#a04040",
  background: "#fbf1f1",
  color: "#5a1e1e",
};

/** Sucesso — verde grande, some sozinho em 4s */
export function toastSuccess(message, description) {
  return sonnerToast.success(message, {
    description,
    duration: 4000,
    style: SUCCESS_STYLE,
    icon: "✓",
  });
}

/** Erro — vermelho grande com CTA "Tentar de novo" (opcional).
 *  onRetry: callback quando o usuário clica "Tentar de novo". Se omitido, sem botão. */
export function toastError(message, { description, onRetry } = {}) {
  return sonnerToast.error(message, {
    description: description || "Verifique sua conexão e tente novamente.",
    duration: 7000,
    style: ERROR_STYLE,
    icon: "⚠",
    action: onRetry
      ? { label: "Tentar de novo", onClick: onRetry }
      : undefined,
  });
}

/** Loading — mantém o toast em tela até chamar `.dismiss()`. Útil pra uploads. */
export function toastLoading(message) {
  const id = sonnerToast.loading(message, {
    duration: Infinity,
    style: { ...BIG_STYLE, borderColor: "#666", background: "#fafaf8", color: "#333" },
  });
  return { dismiss: () => sonnerToast.dismiss(id), id };
}

/** Info azul (navegação) — usar pra transições/links */
export function toastInfo(message, description) {
  return sonnerToast.info(message, {
    description,
    duration: 3500,
    style: { ...BIG_STYLE, borderColor: "#2e5f8f", background: "#eef4fa", color: "#1a3a5c" },
    icon: "ℹ",
  });
}

/** Extrai mensagem de erro amigável de qualquer exceção axios/fetch */
export function pickErrorMsg(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (err?.message) return err.message;
  return "Erro inesperado";
}
